// frontend/src/pages/StoreDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TornaAlMenu from "../components/TornaAlMenu";

const API_URL = process.env.REACT_APP_API_URL || "";

const monthNames = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];

export default function StoreDetail() {
  const nav = useNavigate();
  const { type, id } = useParams(); // type: FRANCHISING | AZIENDA | PV
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUnpaidAll, setShowUnpaidAll] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access");
      const url = `${API_URL}/api/kpi/stores/detail/?type=${encodeURIComponent(type)}&id=${encodeURIComponent(id)}&year=${year}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "Errore caricamento");
      setData(json);
    } catch (e) {
      alert(e.message || "Errore");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [type, id, year]);

  const maxTot = useMemo(() => {
    if (!data?.months?.length) return 0;
    return Math.max(...data.months.map(m => m.total || 0), 0);
  }, [data]);

  const exportMonthsCsv = () => {
    if (!data) return;
    const rows = [
      ["Mese","Ordini","Totale","Insoluti","Provvigioni"],
      ...(data.months || []).map(m => [
        `${String(m.month).padStart(2,"0")}`,
        m.orders || 0,
        Number(m.total || 0).toFixed(2),
        Number(m.unpaid || 0).toFixed(2),
        Number(m.commission || 0).toFixed(2),
      ])
    ];
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], {type:"text/csv;charset=utf-8"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `store_${type}_${id}_${year}_months.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const exportRecentCsv = () => {
    if (!data) return;
    const rows = [
      ["ID","Data","Stato","Totale","Pagato?","Provvigione"],
      ...(data.recent_orders || []).map(r => [
        r.id, r.date, r.status, Number(r.total || 0).toFixed(2), r.is_paid ? "SI" : "NO",
        Number(r.commission_amount || 0).toFixed(2),
      ])
    ];
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], {type:"text/csv;charset=utf-8"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `store_${type}_${id}_${year}_recent.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const exportUnpaidAllCsv = () => {
    if (!data?.unpaid?.all_years) return;
    const rows = [
      ["ID","Data","Stato","Totale","Provvigione"],
      ...(data.unpaid.all_years.orders || []).map(r => [
        r.id, r.date, r.status, Number(r.total || 0).toFixed(2),
        Number(r.commission_amount || 0).toFixed(2),
      ])
    ];
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], {type:"text/csv;charset=utf-8"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `store_${type}_${id}_unpaid_all_years.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const hasOlderUnpaid =
    !!data?.unpaid &&
    (data.unpaid.all_years?.count || 0) > (data.unpaid.year?.count || 0);

  return (
    <div className="container mt-4">
      <button className="btn btn-outline-secondary mb-3" onClick={() => nav("/dashboard-store")}>← Torna alla lista</button>

      {loading ? (
        <p className="text-muted">Caricamento…</p>
      ) : !data ? (
        <div className="alert alert-danger">Errore caricamento.</div>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="m-0">
              🏷️ {data.store.name} <span className="badge bg-secondary ms-2">{data.store.type}</span>
              {data.store.code && <span className="badge bg-light text-dark ms-2">{data.store.code}</span>}
            </h2>
            <div className="d-flex gap-2">
              <select className="form-select" value={year} onChange={e => setYear(Number(e.target.value))}>
                {[0,1,2,3,4,5].map(off => now.getFullYear()-off).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <button className="btn btn-outline-primary" onClick={fetchData}>🔁 Aggiorna</button>
            </div>
          </div>

          {/* Avviso presenza insoluti storici */}
          {hasOlderUnpaid && (
            <div className="alert alert-warning d-flex justify-content-between align-items-center">
              <div>
                <strong>Attenzione:</strong> ci sono insoluti di anni precedenti.
              </div>
              <button className="btn btn-sm btn-outline-dark" onClick={() => setShowUnpaidAll(true)}>
                Mostra insoluti storici
              </button>
            </div>
          )}

          {/* Summary cards */}
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <div className="card shadow-sm"><div className="card-body">
                <div className="text-muted small"># Ordini</div>
                <div className="display-6">{data.summary.orders}</div>
              </div></div>
            </div>
            <div className="col-md-3">
              <div className="card shadow-sm"><div className="card-body">
                <div className="text-muted small">Fatturato {year}</div>
                <div className="display-6">€ {Number(data.summary.revenue || 0).toFixed(2)}</div>
              </div></div>
            </div>
            <div className="col-md-3">
              <div className="card shadow-sm"><div className="card-body">
                <div className="text-muted small">Insoluti {year}</div>
                <div className={`display-6 ${Number(data.summary.unpaid||0)>0 ? "text-danger" : ""}`}>
                  € {Number(data.summary.unpaid || 0).toFixed(2)}
                </div>
              </div></div>
            </div>
            <div className="col-md-3">
              <div className="card shadow-sm"><div className="card-body">
                <div className="text-muted small">Provvigioni {year}</div>
                <div className="display-6">€ {Number(data.summary.commission || 0).toFixed(2)}</div>
              </div></div>
            </div>
          </div>

          {/* Mini chart mesi: barra blu (totale) + overlay rosso (insoluti) */}
          <div className="card shadow-sm mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Andamento {year}</strong>
              <div className="d-flex gap-2">
                <button className="btn btn-sm btn-outline-secondary" onClick={exportMonthsCsv}>⤓ CSV</button>
              </div>
            </div>
            <div className="card-body">
              {data.months.length === 0 ? (
                <div className="text-muted">Nessun dato.</div>
              ) : (
                <div className="list-group">
                  {data.months.map((m, i) => {
                    const percTot = maxTot > 0 ? Math.round((m.total / maxTot) * 100) : 0;
                    const percUnp = maxTot > 0 ? Math.round((m.unpaid / maxTot) * 100) : 0;
                    return (
                      <div key={i} className="list-group-item">
                        <div className="d-flex justify-content-between align-items-center">
                          <div style={{width: 60}}><strong>{monthNames[m.month-1]}</strong></div>
                          <div className="flex-grow-1 mx-3">
                            <div style={{ height: 12, background: "#e9ecef", borderRadius: 8, position: "relative", overflow: "hidden" }}>
                              {/* Totale */}
                              <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${percTot}%`, background: "#0d6efd" }} />
                              {/* Insoluti */}
                              {percUnp > 0 && (
                                <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${percUnp}%`, background: "#dc3545", opacity: 0.85 }} />
                              )}
                            </div>
                          </div>
                          <div className="text-nowrap">
                            <span className="me-3">Ordini: {m.orders}</span>
                            <span className="me-3">Tot: € {m.total.toFixed(2)}</span>
                            <span className={`me-3 ${m.unpaid > 0 ? "text-danger" : ""}`}>Insol.: € {m.unpaid.toFixed(2)}</span>
                            <span>Provv.: € {m.commission.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Ultimi ordini */}
          <div className="card shadow-sm mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Ultimi ordini</strong>
              <button className="btn btn-sm btn-outline-secondary" onClick={exportRecentCsv}>⤓ CSV</button>
            </div>
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Data</th>
                    <th>Stato</th>
                    <th className="text-end">Totale</th>
                    <th className="text-end">Provvigione</th>
                    <th>Pagato</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.recent_orders || []).map(r => (
                    <tr key={r.id}>
                      <td>#{r.id}</td>
                      <td>{r.date}</td>
                      <td><span className="badge bg-secondary">{(r.status || "").toUpperCase()}</span></td>
                      <td className="text-end">€ {Number(r.total || 0).toFixed(2)}</td>
                      <td className="text-end">€ {Number(r.commission_amount || 0).toFixed(2)}</td>
                      <td>{r.is_paid ? <span className="badge bg-success">Sì</span> : <span className="badge bg-warning text-dark">No</span>}</td>
                    </tr>
                  ))}
                  {(!data.recent_orders || data.recent_orders.length === 0) && (
                    <tr><td colSpan={6} className="text-muted">Nessun ordine recente.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Insoluti storici (tutti gli anni) */}
          <div className="card shadow-sm">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Insoluti storici (tutti gli anni)</strong>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted small">
                  #{data.unpaid?.all_years?.count || 0} — € {Number(data.unpaid?.all_years?.amount || 0).toFixed(2)}
                </span>
                <button className="btn btn-sm btn-outline-secondary" onClick={exportUnpaidAllCsv}>⤓ CSV</button>
                <button
                  className="btn btn-sm btn-outline-dark"
                  onClick={() => setShowUnpaidAll(v => !v)}
                >
                  {showUnpaidAll ? "Nascondi" : "Mostra"}
                </button>
              </div>
            </div>
            {showUnpaidAll && (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Data</th>
                      <th>Stato</th>
                      <th className="text-end">Totale</th>
                      <th className="text-end">Provvigione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.unpaid?.all_years?.orders || []).map(r => (
                      <tr key={`u-${r.id}`} className="table-warning">
                        <td>#{r.id}</td>
                        <td>{r.date}</td>
                        <td><span className="badge bg-warning text-dark">{(r.status || "").toUpperCase()}</span></td>
                        <td className="text-end">€ {Number(r.total || 0).toFixed(2)}</td>
                        <td className="text-end">€ {Number(r.commission_amount || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                    {(!data.unpaid?.all_years?.orders || data.unpaid.all_years.orders.length === 0) && (
                      <tr><td colSpan={5} className="text-muted">Nessun insoluto storico.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
